import { db } from "../../firebase";
import { doc, getDoc, updateDoc, deleteDoc, collection, addDoc, query, where, getDocs, arrayUnion } from "firebase/firestore";
import { generateMergeData } from "./performMerge";
import { DestinationRankings } from "../survey-helpers/suggestion-algorithm/suggestion-data";

const performGroupMerge = async (userId, userResult, request) => {
    try {
        //Get request data
        const groupMergeRequestDoc = doc(db, `Groups/${request.groupId}/mergeRequests/${request.groupRequestId}`);
        const groupMergeRequestSnapshot = await getDoc(groupMergeRequestDoc);
    
        let groupMergeRequestData;
        if (groupMergeRequestSnapshot.exists()) {
            groupMergeRequestData = groupMergeRequestSnapshot.data();
        } else {
            throw new Error("Group request doc does not exist")
        }

        //Check if user is first to respond
        const isFirstResponder = !groupMergeRequestData.mergeDocId;

        //If they are first to respond, get friend's result data and merge
        //If not, use existing group merge data
        let groupResult;
        if (isFirstResponder) {
            const friendResultDoc = doc(db,`Results/${groupMergeRequestData.sentBy}/${groupMergeRequestData.subcollection}/${groupMergeRequestData.resultId}`);
            const friendResultSnapshot = await getDoc(friendResultDoc);

            if (friendResultSnapshot.exists()) {
                groupResult = friendResultSnapshot.data();
            } else {
                throw new Error("Friend result document does not exist");
            }
        } else {
            const groupResultDoc = doc(db, `Merges/${groupMergeRequestData.mergeDocId}`);
            const groupResultSnapshot = await getDoc(groupResultDoc);

            if (groupResultSnapshot.exists()) {
                groupResult = groupResultSnapshot.data();
            } else {
                throw new Error("Group merge document does not exist");
            }
        }

        //Perform merge
        let mergedData;
        if (isFirstResponder) {
            //Standard merge
            mergedData = generateMergeData(userResult, groupResult);
        } else {
            //Group merge
            const userAttributeTotals = {};
            let userSelectionCount = 0;

            //Count total selections for average
            for (const key in userResult.selectionCounts) {
                const selection = userResult.selectionCounts[key];
                userSelectionCount += selection.selected + selection.notSelected;
            };

            //Calculate average
            for (const [key, total] of Object.entries(userResult.attributeTotals)) {
                const average = total / userSelectionCount;
                userAttributeTotals[key] = average;
            };

            //Add user averages to existing group totals
            const mergedAttributeTotals = { ...groupResult.attributeTotals };
            for (const key in userAttributeTotals) {
                if (mergedAttributeTotals[key] !== undefined) {
                    mergedAttributeTotals[key] += userAttributeTotals[key];
                } else {
                    mergedAttributeTotals[key] = userAttributeTotals[key];
                }
            }

            //Calculate rankings
            const rankings = {};
            for (const destination in DestinationRankings) {
                if (DestinationRankings.hasOwnProperty(destination)) {
                    const destinationData = DestinationRankings[destination];
                    let score = 0;
                
                    for (const attribute in mergedAttributeTotals) {
                        if (mergedAttributeTotals.hasOwnProperty(attribute) && destinationData.hasOwnProperty(attribute)) {
                            score += mergedAttributeTotals[attribute] * destinationData[attribute];
                        }
                    }
                
                    rankings[destination] = score;
                }
            };

            const sortedRankings = Object.entries(rankings).sort(([, scoreA], [, scoreB]) => scoreB - scoreA);
            const top8Destinations = sortedRankings.slice(0, 8).map(item => item[0]);

            mergedData = {
                mergedAttributeTotals,
                top8Destinations
            };
        }

        //Post data
        let mergeDocData;
        if (isFirstResponder) {
            //Create merge doc
            const mergesColRef = collection(db, "Merges");
            const mergeName = `${request.name}'s merge`;

            mergeDocData = {
                groupId: request.groupId,
                name: mergeName,
                attributeTotals: mergedData.mergedAttributeTotals,
                top8Destinations: mergedData.top8Destinations,
                includedMembers: [userId, groupMergeRequestData.sentBy],
                createdAt: new Date()
            };

            const newMergeDoc = await addDoc(mergesColRef, mergeDocData);

            //Update mergeDocId in group merge request
            await updateDoc(groupMergeRequestDoc, {
                mergeDocId: newMergeDoc.id
            });
        } else {
            //Update merge doc
            const groupMergeDoc = doc(db, "Merges", groupMergeRequestData.mergeDocId);
            
            await updateDoc(groupMergeDoc, {
                attributeTotals: mergedData.mergedAttributeTotals,
                top8Destinations: mergedData.top8Destinations,
                includedMembers: arrayUnion(userId) 
            });

            //Fetch the updated document to return data
            const updatedGroupMergeDocSnapshot = await getDoc(groupMergeDoc);

            if (updatedGroupMergeDocSnapshot.exists()) {
                mergeDocData = updatedGroupMergeDocSnapshot.data();
            } else {
                throw new Error("Group merge document does not exist after update.");
            }
        }

        //Delete user merge request doc
        const mergeRequestCol = collection(db, `Users/${userId}/mergeRequests`);

        //Must query because of auto-generated docId
        const q = query(
            mergeRequestCol, 
            where("groupId", "==", request.groupId), 
            where("groupRequestId", "==", request.groupRequestId)
        );

        const querySnapshot = await getDocs(q);

        //Check if a document is found and delete it
        if (!querySnapshot.empty) {
            //Get the first document (should only be one)
            const docSnap = querySnapshot.docs[0];
            
            await deleteDoc(doc(db, `Users/${userId}/mergeRequests`, docSnap.id));
        } else {
            console.log("No merge invite found to delete.");
        }

        //After success, update group merge doc with new included member
        //If all members are included, delete doc
        if (request.includedMembers.length == request.groupLength) {
            await deleteDoc(groupMergeRequestDoc);
        } else {
            await updateDoc(groupMergeRequestDoc, {
                includedMembers: arrayUnion(userId)
            })
        }

        return mergeDocData;
    } catch (error) {
        console.error("Error performing group merge:", error.message);
    }
};

export default performGroupMerge;